import { useAppState } from 'components/contexts/State';
import React, { useEffect, useState } from 'react';
import LargeFileWarningModal from '../../common/LargeFileWarningModal';
import { createUploadSignedURL } from '../Services/DataRepoService';
import ShpFileWarningModal from '../../common/ShpFileWarningModal';
import IncompatibleFileFormatModal from '../../common/IncompatibleFileFormatModal';
import DuplicateFileModal from '../../common/DuplicateFileModal';

const CHUNK_SIZE = 1024 * 1024; // 1MB

function FileUploadExplorer() {
  const {
    openFileUploadExplorer,
    setOpenFileUploadExplorer,
    setDisabledUploadDataBtn,
    setSignedUploadURL,
    setCurrProcessId,
    selectedFileType,
    setSelectedFileType,
    setUploadStatusDetails,
    setFileTypeFromSocket,
    setFileNameFromSocket,
    setCurrentUploadXhr,
    setIsFetchingSignedUrl,
    setIsOpenUploadNotification,
    setCancelUploadModalOpen,
  } = useAppState();

  const [file, setFile] = useState(null);
  const [showLargeFileWarning, setShowLargeFileWarning] = useState(false);
  const [showShpFileWarning, setShowShpFileWarning] = useState(false);
  const [duplicateFileWarning, setDuplicateFileWarning] = useState(false);

  const [showIncompatibleFormatWarning, setShowIncompatibleFormatWarning] =
    useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop();

      const shpValidationCheck = ['shp'];
      if (shpValidationCheck.includes(fileExtension)) {
        setShowShpFileWarning(true);
        return;
      }

      const validExtensions = [
        'gdb',
        'geojson',
        'gpkg',
        'zip',
        'docx',
        'html',
        'json',
        'pdf',
        'pptx',
        'txt',
        'xhtml',
        'xlsx',
        'xml',
      ];

      if (!validExtensions.includes(fileExtension)) {
        setShowIncompatibleFormatWarning(true);
        return;
      }

      if (selectedFile.size > 2 * 1024 * 1024 * 1024) {
        setShowLargeFileWarning(true);
        setFile(selectedFile);
        setSelectedFileType(
          ['gdb', 'geojson', 'gpkg', 'zip'].includes(fileExtension) ? 'table' : 'document'
        );
      } else if (['gdb', 'geojson', 'gpkg', 'zip'].includes(fileExtension)) {
        setFile(selectedFile);
        setSelectedFileType('table');
        setDisabledUploadDataBtn(true);
        uploadData(selectedFile, 'table');
      } else if (
        ['docx', 'html', 'json', 'pdf', 'pptx', 'txt', 'xhtml', 'xlsx', 'xml'].includes(
          fileExtension
        )
      ) {
        setFile(selectedFile);
        setSelectedFileType('document');
        setDisabledUploadDataBtn(true);
        uploadData(selectedFile, 'document');
      }
    } else {
      setDisabledUploadDataBtn(false);
    }

    setOpenFileUploadExplorer(false);
  };

  const getMimeType = (fileExtension) => {
    const mimeTypes = {
      csv: 'text/csv',
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      zip: 'application/zip',
      html: 'text/html',
      txt: 'text/plain',
      json: 'application/json',
      xhtml: 'application/xhtml+xml',
      xml: 'application/xml',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };
    return mimeTypes[fileExtension] || 'application/octet-stream';
  };

  const uploadFullFile = (file, signedUrl, processId, fileType) => {
    const xhr = new XMLHttpRequest();
    setCurrentUploadXhr(xhr);

    return new Promise((resolve, reject) => {
      const fileExtension = file.name.split('.').pop();
      const mimeType = getMimeType(fileExtension);

      xhr.open('PUT', signedUrl, true);
      xhr.setRequestHeader('Content-Type', mimeType);
      xhr.setRequestHeader('x-goog-meta-process-id', processId);
      xhr.setRequestHeader('x-goog-meta-file-type', fileType);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 50;
          if (Math.round(progress) === 50) setCancelUploadModalOpen(false);
          setUploadStatusDetails([
            {
              step: 'Upload',
              status: 'In progress',
              message: '',
              percentage_complete: Math.round(progress),
            },
            {
              step: 'Add details',
              status: 'Not started',
              message: '',
            },
            {
              step: 'Finalize',
              status: 'Not started',
              message: '',
            },
          ]);
        }
      };

      xhr.onload = () => {
        setCurrentUploadXhr(null);
        if (xhr.status === 200 || xhr.status === 201) {
          resolve(true);
        } else {
          reject(`Upload failed with status ${xhr.status}`);
        }
      };

      xhr.onerror = () => {
        setCurrentUploadXhr(null);
        reject('Error uploading file.');
      };

      xhr.onabort = () => {
        setCurrentUploadXhr(null);
        setSignedUploadURL('');
        setUploadStatusDetails([]);
        setCurrProcessId('');
        setDisabledUploadDataBtn(false);
        setFileTypeFromSocket('');
        setFileNameFromSocket('');
        reject('Upload aborted by user.');
      };

      xhr.send(file);
    });
  };

  const uploadData = async (file, type) => {
    try {
      setIsFetchingSignedUrl(true);
      const URIDetails = await createUploadSignedURL(file.name, type);
      setIsFetchingSignedUrl(false);

      setCurrProcessId(URIDetails.process_id);
      setSignedUploadURL(URIDetails.signed_url);

      setUploadStatusDetails([
        {
          step: 'Upload',
          status: 'In progress',
          message: '',
          percentage_complete: 0,
        },
        {
          step: 'Add details',
          status: 'Not started',
          message: '',
        },
        {
          step: 'Finalize',
          status: 'Not started',
          message: '',
        },
      ]);
      setFileTypeFromSocket(type);
      setFileNameFromSocket(URIDetails.file_name);
      setIsOpenUploadNotification(true);

      try {
        await uploadFullFile(file, URIDetails.signed_url, URIDetails.process_id, type);
      } catch (error) {
        console.error('Failed to upload file:', error);
      }
    } catch (error) {
      if (error.status === 409) {
        setDuplicateFileWarning(true);
      }
      console.error('Error creating upload URL:', error);
      setIsFetchingSignedUrl(false);
    }
  };

  const openFileDialog = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept =
      '.gdb, .geojson, .gpkg, .zip, .docx, .html, .pdf, .json, .pptx, .txt, .xhtml, .xlsx, .xml';
    input.onchange = handleFileChange;
    input.click();

    setTimeout(() => {
      if (!input.files.length) {
        setOpenFileUploadExplorer(false);
        setDisabledUploadDataBtn(false);
      }
    }, 100);
  };

  const handleContinue = () => {
    setShowLargeFileWarning(false);
    setDisabledUploadDataBtn(true);
    if (file && selectedFileType) {
      uploadData(file, selectedFileType);
    }
  };

  const handleClose = () => {
    setShowLargeFileWarning(false);
    resetState();
  };

  const resetState = () => {
    setDisabledUploadDataBtn(false);
    setFile(null);
    setSelectedFileType('');
  };

  if (openFileUploadExplorer) {
    openFileDialog();
  }

  const handleShpClose = () => {
    setShowShpFileWarning(false);
    setDisabledUploadDataBtn(false);
  };

  const handleIncompatibleClose = () => {
    setShowIncompatibleFormatWarning(false);
    setDisabledUploadDataBtn(false);
  };

  const handleDuplicateFileModalClose = () => {
    setDuplicateFileWarning(false);
    setDisabledUploadDataBtn(false);
  };

  return (
    <>
      <LargeFileWarningModal
        open={showLargeFileWarning}
        onClose={handleClose}
        onContinue={handleContinue}
      />
      <ShpFileWarningModal open={showShpFileWarning} onClose={handleShpClose} />

      <IncompatibleFileFormatModal
        open={showIncompatibleFormatWarning}
        onClose={handleIncompatibleClose}
      />
      <DuplicateFileModal
        open={duplicateFileWarning}
        onClose={handleDuplicateFileModalClose}
      />
    </>
  );
}

export default FileUploadExplorer;
